import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, Loader2, RefreshCw, Zap, Clock, MessageSquare, Server,
  CheckCircle, AlertTriangle, XCircle, Cpu, Power
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Performance du LLM self-hosted
// Latences, volume par heure, répartition local/fallback,
// volume par client. Sert à décider QUAND passer au GPU.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

const HEALTH = {
  ok:       { icon: CheckCircle,   color: 'text-green-300',  bg: 'bg-green-500/10 border-green-500/30',  label: 'Tout va bien — infra actuelle suffisante' },
  warning:  { icon: AlertTriangle, color: 'text-yellow-300', bg: 'bg-yellow-500/10 border-yellow-500/30', label: 'À surveiller — préparez le palier suivant' },
  critical: { icon: XCircle,       color: 'text-red-300',    bg: 'bg-red-500/10 border-red-500/30',      label: 'Saturation — passez au serveur GPU' },
};

export default function LLMPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restarting, setRestarting] = useState(false);
  const [restartResult, setRestartResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/llm-performance`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setData(d);
    } finally { setLoading(false); }
  }, []);

  const restartGemma = useCallback(async () => {
    setRestarting(true);
    setRestartResult(null);
    try {
      const res = await fetch(`${API_URL}/api/bot/gemma-restart`, { method: 'POST', headers: authHeaders() });
      const d = await res.json();
      setRestartResult({ ok: d.success, message: d.message });
      if (d.success) setTimeout(load, 8000); // laisse Ollama redemarrer avant de re-verifier
    } catch (e) {
      setRestartResult({ ok: false, message: "Erreur reseau pendant la demande." });
    } finally {
      setRestarting(false);
    }
  }, [load]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh auto chaque minute
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) {
    return <div className="py-16 text-center"><Loader2 className="w-10 h-10 text-blue-400 animate-spin mx-auto" /></div>;
  }
  if (!data) {
    return <p className="text-gray-400 py-10 text-center">Impossible de charger les statistiques.</p>;
  }

  const h = HEALTH[data.health] || HEALTH.ok;
  const HIcon = h.icon;
  const s24 = data.stats_24h;
  const s7 = data.stats_7d;
  const maxCount = Math.max(1, ...data.hourly.map(x => x.count));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-3">
            <Activity className="w-8 h-8 text-cyan-400" />
            Performance LLM
          </h1>
          <p className="text-sm text-blue-200">
            Latences et volume de l'agent IA (serveur self-hosted + fallbacks). Les seuils indiquent quand passer au palier supérieur (GPU).
          </p>
        </div>
        <button onClick={load} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white self-start">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ── SANTÉ GLOBALE ── */}
      <div className={`p-4 rounded-2xl border flex items-start gap-3 ${h.bg}`}>
        <HIcon className={`w-6 h-6 flex-shrink-0 ${h.color}`} />
        <div>
          <div className={`font-bold text-sm ${h.color}`}>{h.label}</div>
          {data.issues.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {data.issues.map((iss, i) => <li key={i} className="text-xs text-gray-300">• {iss}</li>)}
            </ul>
          )}
          {data.issues.length === 0 && (
            <p className="text-xs text-gray-400 mt-0.5">
              Seuils surveillés : latence moyenne &gt; 15s, P95 &gt; 30s, part du LLM local &lt; 70%.
            </p>
          )}
        </div>
      </div>

      {/* ── GEMMA EN DIRECT : verifie MAINTENANT, independamment du trafic recent ── */}
      {data.gemma_live && (
        <div className={`p-4 rounded-2xl border ${
          data.gemma_live.reachable ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              {data.gemma_live.reachable
                ? <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                : <XCircle className="w-5 h-5 text-red-300 flex-shrink-0" />}
              <div>
                <div className={`font-bold text-sm ${data.gemma_live.reachable ? 'text-green-300' : 'text-red-300'}`}>
                  Gemma ({data.gemma_live.model}) — {data.gemma_live.reachable ? 'en ligne maintenant' : 'injoignable maintenant'}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {data.gemma_live.reachable
                    ? `Répond en ${data.gemma_live.latency_s}s (tunnel PC → serveur). Vérifié à l'instant, pas juste sur d'anciens messages.`
                    : (data.gemma_live.error || "Le PC de Hery ne répond pas via le tunnel.")}
                </div>
              </div>
            </div>
            {!data.gemma_live.reachable && (
              <button
                onClick={restartGemma}
                disabled={restarting}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-200 text-xs font-bold disabled:opacity-50 flex-shrink-0"
              >
                {restarting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
                Relancer Gemma sur le PC
              </button>
            )}
          </div>
          {restartResult && (
            <div className={`text-xs mt-2 ${restartResult.ok ? 'text-green-300' : 'text-yellow-300'}`}>
              {restartResult.ok ? '✅ ' : '⚠️ '}{restartResult.message}
            </div>
          )}
        </div>
      )}

      {/* ── CARTES 24H ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={MessageSquare} label="Messages (24h)" value={s24.messages}
          sub={`${s7.messages} sur 7 jours`} />
        <StatCard icon={Clock} label="Latence moyenne (24h)" value={s24.avg_s !== null ? `${s24.avg_s}s` : '—'}
          sub={s24.p95_s !== null ? `P95 : ${s24.p95_s}s · max : ${s24.max_s}s` : 'pas encore de données'}
          alert={s24.avg_s !== null && s24.avg_s > 15} />
        <StatCard icon={Cpu} label="LLM local (24h)" value={s24.local_share_pct !== null ? `${s24.local_share_pct}%` : '—'}
          sub="part des réponses servies par Gemma"
          alert={s24.local_share_pct !== null && s24.messages >= 10 && s24.local_share_pct < 70} />
        <StatCard icon={Zap} label="Coût fallbacks (7j)" value={`$${s7.cost_usd}`}
          sub={`${(s7.tokens / 1000).toFixed(0)}k tokens estimés`} />
      </div>

      {/* ── HISTOGRAMME PAR HEURE (24h) ── */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-cyan-300" /> Volume par heure (dernières 24h)
        </h2>
        <div className="flex items-end gap-1 h-32">
          {data.hourly.map((b, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div className="absolute -top-9 hidden group-hover:block bg-slate-800 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap z-10">
                {b.count} msg{b.avg_s !== null ? ` · ${b.avg_s}s en moy.` : ''}
              </div>
              <div
                className={`w-full rounded-t transition-all ${
                  b.avg_s !== null && b.avg_s > 15 ? 'bg-yellow-500/70' : 'bg-cyan-500/60 group-hover:bg-cyan-400'
                }`}
                style={{ height: `${Math.max(b.count > 0 ? 6 : 2, (b.count / maxCount) * 100)}%` }}
              />
              {i % 4 === 0 && <span className="text-gray-500 text-[10px]">{b.hour}</span>}
            </div>
          ))}
        </div>
        <p className="text-gray-500 text-xs mt-2">Barres jaunes = heures où la latence moyenne dépasse 15s (signe de file d'attente).</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* ── PROVIDERS ── */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h2 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
            <Server className="w-4 h-4 text-cyan-300" /> Répartition par moteur (7 jours)
          </h2>
          {Object.keys(s7.providers).length === 0 ? (
            <p className="text-gray-500 text-sm">Aucune donnée.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(s7.providers).sort((a, b) => b[1] - a[1]).map(([prov, count]) => {
                const pct = Math.round(count * 100 / s7.messages);
                const isLocal = prov.startsWith('local');
                return (
                  <div key={prov}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className={isLocal ? 'text-green-300' : 'text-yellow-300'}>
                        {isLocal ? '🖥️ ' : '☁️ '}{prov}
                      </span>
                      <span className="text-gray-400">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className={`h-full ${isLocal ? 'bg-green-500' : 'bg-yellow-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              <p className="text-gray-500 text-xs pt-1">
                🖥️ local = gratuit (notre serveur) · ☁️ fallback = payant au token. Si la part fallback grimpe, le serveur local sature.
              </p>
            </div>
          )}
        </div>

        {/* ── TOP CLIENTS ── */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h2 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-cyan-300" /> Volume par client (7 jours)
          </h2>
          {data.top_clients.length === 0 ? (
            <p className="text-gray-500 text-sm">Aucune donnée.</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {data.top_clients.map(c => (
                <div key={c.client_id} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2">
                  <span className="text-white text-xs truncate">{c.name}</span>
                  <span className="text-gray-400 text-xs flex-shrink-0 ml-2">{c.messages} msg</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── GUIDE DES PALIERS ── */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 text-blue-200 text-xs leading-relaxed">
        <strong className="text-blue-100">Plan de scaling :</strong> l'infra actuelle (CPU 16 cœurs) tient confortablement
        jusqu'à ~30 pages actives. Signaux pour passer au GPU (Hetzner GEX44, ~184€/mois) : latence moyenne 24h
        durablement &gt; 15s, P95 &gt; 30s aux heures de pointe, ou part du LLM local &lt; 70% (fallbacks fréquents = serveur saturé).
        Ce panneau se rafraîchit automatiquement chaque minute.
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, alert = false }) {
  return (
    <div className={`bg-white/5 border rounded-2xl p-4 ${alert ? 'border-yellow-500/40' : 'border-white/10'}`}>
      <div className="text-gray-400 text-xs mb-1 flex items-center gap-1.5">
        <Icon className={`w-3.5 h-3.5 ${alert ? 'text-yellow-300' : ''}`} /> {label}
      </div>
      <div className={`font-bold text-2xl ${alert ? 'text-yellow-300' : 'text-white'}`}>{value}</div>
      {sub && <div className="text-gray-500 text-xs mt-0.5">{sub}</div>}
    </div>
  );
}
